/* eslint-disable max-lines */
import { toast } from '@heidi/design-system/toast';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { useTranslations } from '@heidi/runtime/i18n';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useRouter } from 'next/router';
import { useChatFeedbackMutation } from '../hooks/useChatFeedbackMutation';
import { useChatSessionId } from '../hooks/useChatSessionId';
import { useCustomerFeedback } from '../hooks/useCustomerFeedback';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import type { SessionConversation } from '../types/types';
import { createChatSnapshot } from '../utils/chatSnapshot';
import { extractMessageId } from '../utils/extractMessageId';
import { createFeedbackConfig } from '../utils/feedbackConfig';
import { FeedbackPopover } from './FeedbackPopover';
import { track } from '@/lib/analytics/track';

export type FeedbackType = 'positive' | 'negative' | null;

interface CustomerFeedbackProps {
  onFeedback?: (
    feedback: FeedbackType,
    comment?: string,
    conversationData?: string
  ) => void;
  initialFeedback?: FeedbackType;
  disabled?: boolean;
  className?: string;
  conversation?: SessionConversation;
  position?: number;
  mode?: string;
}

// Hook for ASK_AI feedback using the dedicated /chat/feedback endpoint
const useCSATFeedback = ({
  onFeedback,
  conversation,
  position,
  mode,
}: {
  onFeedback?: (
    feedback: FeedbackType,
    comment?: string,
    conversationData?: string
  ) => void;
  conversation?: SessionConversation;
  position?: number;
  mode?: string;
}) => {
  const sessionId = useChatSessionId();
  const router = useRouter();
  const { t } = useTranslations();
  const { baseProps, trackChatError } = useEvidenceAnalytics();
  const { mutateAsync: submitFeedback, isPending: isSubmitting } =
    useChatFeedbackMutation();

  // Get chatId from URL only on standalone chat pages (/chat/[id])
  const isStandaloneChatPage = router.pathname.includes('/chat');
  const urlChatId =
    isStandaloneChatPage && router.query.id
      ? Array.isArray(router.query.id)
        ? router.query.id[0]
        : router.query.id
      : undefined;

  const handleSubmitFeedback = async (feedback: FeedbackType, comment: string) => {
    if (!conversation) {
      console.error('Missing conversation for feedback submission');
      toast({
        tone: 'destructive',
        title: t('feedback.askAiFeedback.feedbackError'),
        description: t('feedback.askAiFeedback.unableToSubmit'),
      });
      return;
    }

    // Create chat snapshot for content_snapshot
    const conversationData = createChatSnapshot(conversation, position, mode);

    // Extract message_id from the timeline entry for analytics tracking
    const messageId = extractMessageId(conversation, position);

    // Resolve mode: only send 'legacy' or 'evidence', not 'auto'
    const resolvedMode = mode === 'legacy' || mode === 'evidence' ? mode : undefined;

    // Resolve chatId: URL param (standalone) or metadata (in-session)
    const effectiveChatId = urlChatId ?? conversation.metadata?.clientChatId;

    // Submit to /chat/feedback (unified for both sessionless and in-session)
    if (effectiveChatId) {
      try {
        await submitFeedback({
          target_id: effectiveChatId,
          session_id: sessionId || undefined,
          message_id: messageId ?? null,
          feedback: {
            rating: feedback === 'positive' ? 'thumb_up' : 'thumb_down',
            user_feedback: comment || null,
            mode: resolvedMode,
          },
          content_snapshot: conversationData,
        });
      } catch (error) {
        console.error('Failed to submit chat feedback:', error);
        trackChatError({
          errorType: 'feedback',
          errorMessage:
            error instanceof Error ? error.message : 'Failed to submit feedback',
        });
        toast({
          tone: 'destructive',
          title: t('feedback.askAiFeedback.feedbackError'),
          description: t('feedback.askAiFeedback.failedToSubmit'),
        });
        return;
      }
    } else {
      // No chatId available — log a warning but don't break the UX
      console.warn(
        'Chat feedback skipped: no chatId available from URL or conversation metadata'
      );
    }

    // Track to PostHog (fires for both sessionless and in-session)
    // Note: Dual-tracking for feedback_submitted will be added in a future PR
    // when FeedbackSubmittedEventProps is extended to support Evidence context
    track({ options: { amplitude: false },
      event_name: TrackingEventName.EvidenceResponseFeedbackSubmitted,
      props: {
        ...baseProps,
        feedback_type: feedback ?? 'positive',
        chat_id: effectiveChatId ?? undefined,
        session_id: sessionId ?? undefined,
        message_id: messageId,
        has_comment: !!comment && comment.length > 0,
        response_position: position,
        user_comment: comment || undefined,
        mode: resolvedMode,
      },
    });

    // Show success notification
    toast({
      tone: 'success',
      title: t('feedback.askAiFeedback.feedbackSubmitted'),
    });

    // Call the optional callback with the data that was sent
    onFeedback?.(feedback, comment, conversationData);
  };

  return {
    submitFeedback: handleSubmitFeedback,
    hasConversation: !!conversation,
    isSubmitting,
  };
};

/**
 * The `ghost` variant carries no resting colour, so without this the glyphs
 * inherit ambient primary and read heavier than the action buttons sharing
 * this row. Selection is signalled by the outline/filled icon swap, not colour.
 */
const feedbackButtonClasses = 'text-text-secondary hover:text-text-primary';

export const CustomerFeedback = ({
  onFeedback,
  initialFeedback = null,
  disabled = false,
  className,
  conversation,
  position,
  mode,
}: CustomerFeedbackProps) => {
  const { t } = useTranslations();

  // Use the CSAT hook to get conversation data automatically
  const { submitFeedback, hasConversation, isSubmitting } = useCSATFeedback({
    onFeedback,
    conversation,
    position,
    mode,
  });

  // Use the custom hook for state management
  const {
    selectedFeedback,
    isModalOpen,
    feedbackComment,
    pendingFeedback,
    isSubmitDisabled,
    handleFeedbackClick,
    handleSubmit,
    handleCancel,
    setFeedbackComment,
  } = useCustomerFeedback({
    initialFeedback,
    mode,
  });

  const handleFeedback = (feedback: FeedbackType) => {
    if (disabled || !hasConversation) return;
    handleFeedbackClick(feedback);
  };

  const handleSubmitWrapper = async () => {
    await handleSubmit(submitFeedback);
  };

  // Create feedback configuration using translations
  const feedbackConfig = createFeedbackConfig(t);

  const getModalTitle = () => {
    if (pendingFeedback === 'positive') {
      return t('feedback.askAiFeedback.positive.title');
    } else if (pendingFeedback === 'negative') {
      return t('feedback.askAiFeedback.negative.title');
    }
    return t('feedback.askAiFeedback.defaultTitle');
  };

  const getPlaceholderText = () => {
    if (pendingFeedback === 'positive') {
      return t('feedback.askAiFeedback.positive.placeholder');
    } else if (pendingFeedback === 'negative') {
      return t('feedback.askAiFeedback.negative.placeholder');
    }
    return t('feedback.askAiFeedback.defaultPlaceholder');
  };

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      <FeedbackPopover
        feedbackType="positive"
        icon={ThumbsUp}
        filledIcon={feedbackConfig.positive.filledIcon}
        isSelected={selectedFeedback === 'positive'}
        isOpen={isModalOpen && pendingFeedback === 'positive'}
        isSubmitting={isSubmitting}
        disabled={disabled}
        hasConversation={hasConversation}
        feedbackComment={feedbackComment}
        isSubmitDisabled={isSubmitDisabled}
        buttonClassName={feedbackButtonClasses}
        autoSizeSvg
        alignOffset={feedbackConfig.positive.alignOffset}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        onFeedbackClick={handleFeedback}
        onCancel={handleCancel}
        onSubmit={() => void handleSubmitWrapper()}
        onCommentChange={setFeedbackComment}
        getModalTitle={getModalTitle}
        getPlaceholderText={getPlaceholderText}
      />

      <FeedbackPopover
        feedbackType="negative"
        icon={ThumbsDown}
        filledIcon={feedbackConfig.negative.filledIcon}
        isSelected={selectedFeedback === 'negative'}
        isOpen={isModalOpen && pendingFeedback === 'negative'}
        isSubmitting={isSubmitting}
        disabled={disabled}
        hasConversation={hasConversation}
        feedbackComment={feedbackComment}
        isSubmitDisabled={isSubmitDisabled}
        buttonClassName={feedbackButtonClasses}
        autoSizeSvg
        alignOffset={feedbackConfig.negative.alignOffset}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        onFeedbackClick={handleFeedback}
        onCancel={handleCancel}
        onSubmit={() => void handleSubmitWrapper()}
        onCommentChange={setFeedbackComment}
        getModalTitle={getModalTitle}
        getPlaceholderText={getPlaceholderText}
      />
    </div>
  );
};
